import { Box, Button, CircularProgress, Typography } from "@mui/material"
import { DataGrid, GridColDef, GridToolbar } from "@mui/x-data-grid"
import { EUTManagementLeftBox } from "components/EUTManagementLeftBox/EUTManagementLeftBox"
import { EUTManagementRightBox } from "components/EUTManagementRightBox/EUTManagementRightBox"
import { DeviceEutGroup, DeviceType, MovementData, Project } from "graphql/types"
import { useStoreActions, useStoreState } from "hooks/storeHooks"
import { useEffect, useState } from "react"
import { useLocation } from "react-router"
import { allExaminationsMore } from "utils/allExaminations"
import { getCurrentLocation } from "utils/getCurrentLocation"

export const EUTManagement = () => {
  const location = useLocation()
  const [eut, setEut] = useState<DeviceEutGroup | null>(null)
  const [newEut, setNewEut] = useState<DeviceEutGroup | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const eutData: DeviceEutGroup[] = useStoreState((state) => state.eutGroups)
  const { fetchDevicesByType } = useStoreActions((actions) => actions)
  const projectData: Project[] = useStoreState((state) => state.projects)
  const updateEUT = useStoreActions((actions) => actions.updateEutGroup)
  const updateProject = useStoreActions((actions) => actions.updateProject)

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true)
        setError(null)
        await fetchDevicesByType({ type: DeviceType.EUT_GROUP, fresh: true })
      } catch (err) {
        console.error("Fehler beim Laden der EUT-Gruppen:", err)
        setError((err as Error)?.message || "Unbekannter Fehler")
      } finally {
        setLoading(false)
      }
    }
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Auto-select EUT if navigated from ExaminationPage
  useEffect(() => {
    if (location.state?.selectedEutId && eutData.length > 0) {
      const selectedEut = eutData.find((item) => item.id === location.state.selectedEutId)
      if (selectedEut) {
        setEut(selectedEut)
        setNewEut(selectedEut)
      }
    }
  }, [location.state, eutData])

  useEffect(() => {
    if (eut) {
      const newEutData = eutData.find((item) => item.id === eut.id)
      if (newEutData) {
        setEut(newEutData)
        setNewEut(newEutData)
      }
    }
  }, [eutData, eut])

  const columns: GridColDef[] = [
    { field: "barcode", headerName: "Barcode", width: 150 },
    { field: "name", headerName: "Name", width: 250 },
    {
      field: "movementData",
      headerName: "Lagerplatz",
      width: 150,
      valueGetter: (value: MovementData[]) => getCurrentLocation(value),
    },
  ]

  const handleRowClick = (params: any) => {
    const selectedEut = eutData.find((item) => item.id === params.id)
    setEut(selectedEut || null)
  }

  const handleSave = (newDevice: DeviceEutGroup) => {
    if (newDevice) {
      updateEUT({ deviceEutGroup: newDevice, isOffer: false })
      // search the project which uses the EUT
      const project = projectData.filter((proj) =>
        proj.devices.some((item) => item.id === newDevice.id)
      )

      project.forEach((proj) =>
        updateProject({
          ...proj,
          devices: proj.devices.map((dev) => {
            if (dev.id === newDevice.id) {
              return newDevice
            }
            return dev
          }),
        })
      )
    }
  }

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
        }}
      >
        <Typography variant="h6" color="error">
          Fehler beim Laden der EUT-Daten
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {error}
        </Typography>
        <Button
          variant="contained"
          onClick={async () => {
            try {
              setError(null)
              setLoading(true)
              await fetchDevicesByType({ type: DeviceType.EUT_GROUP, fresh: true })
            } catch (err) {
              setError((err as Error)?.message || "Unbekannter Fehler")
            } finally {
              setLoading(false)
            }
          }}
        >
          Erneut versuchen
        </Button>
      </Box>
    )
  }

  return (
    <>
      {!eut && (
        <DataGrid
          rows={eutData}
          columns={columns}
          onRowClick={handleRowClick}
          getRowId={(row) => row.id}
          disableColumnFilter
          disableColumnSelector
          disableDensitySelector
          slots={{ toolbar: GridToolbar }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
              printOptions: { disableToolbarButton: true },
              csvOptions: { disableToolbarButton: true },
            },
          }}
        />
      )}

      {eut && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "center",
            height: "calc(100vh - 155px)",
            overflow: "hidden",
            mb: 0,
          }}
        >
          {newEut && (
            <EUTManagementLeftBox
              eut={eut}
              onSave={handleSave}
              onCancel={() => setEut(null)}
              examinations={allExaminationsMore(
                projectData.filter((p) => p.devices.some((item) => item.id === newEut.id))
              )}
            />
          )}

          {newEut && <EUTManagementRightBox eut={eut} onSave={handleSave} />}
        </Box>
      )}
    </>
  )
}
